/**
 * 跨浏览器同步相关的actions
 * 封装了直播间状态同步、WebSocket连接管理等功能
 */

import { emitter } from "@/utils/emitter/mitt";
import { getLiveRoomDetail } from "@/api/live";
import { ElMessage } from "element-plus";

export function createCrossBrowserSyncActions(store: any) {
  // 防重复调用的状态变量
  let lastNotificationKey = "";

  /**
   * 设置跨浏览器直播状态同步监听
   */
  const setupCrossBrowserSync = () => {
    // 监听直播开始/结束通知
    emitter.on("liveStatusChange", handleLiveStatusChange);
  };

  /**
   * 清理跨浏览器同步监听器
   */
  const cleanupCrossBrowserSync = () => {
    emitter.off("liveStatusChange", handleLiveStatusChange);
  };

  /**
   * 处理start-notification推送的直播状态变化
   */
  const handleLiveStatusChange = (data: {
    action: "start" | "end";
    roomId: string;
    timestamp?: number;
  }) => {
    if (!store.currentRoomState) {
      return;
    }

    const currentRoomId =
      store.currentRoomState.roomId || store.currentRoomState.productList?.id;

    // 防重复处理
    const notificationKey = `${data.action}_${data.roomId}_${data.timestamp || Date.now()}`;

    if (lastNotificationKey === notificationKey) {
      return;
    }
    lastNotificationKey = notificationKey;

    // 如果是当前房间的状态变化
    if (data.roomId === currentRoomId) {
      if (data.action === "start") {
        if (store.currentRoomState.productList) {
          store.currentRoomState.productList.status = "live";
        }
        // 初始化直播相关连接
        initLiveConnections();
      } else if (data.action === "end") {
        if (store.currentRoomState.productList) {
          store.currentRoomState.productList.status = "ended";
        }
        // 清理直播相关连接
        cleanupLiveConnections();
      }
    }
  };

  /**
   * 初始化直播连接
   */
  const initLiveConnections = async () => {
    if (!store.currentRoomState) return;

    const roomId =
      store.currentRoomState.roomId || store.currentRoomState.productList?.id;
    if (!roomId) return;
    try {
      // 1. 检查sessionId（音频队列WebSocket需要）
      if (!store.currentRoomState.sessionId) {
        console.warn(`⚠️ 跳过初始化直播连接：房间${roomId}缺少sessionId`);
        return;
      }

      // 2. 获取直播间详情（获取绑定的直播间地址）
      try {
        const detailResult = await getLiveRoomDetail({ roomId });

        if (detailResult && detailResult.success && detailResult.result) {
          const liveRoomDetail = detailResult.result;

          // 更新直播链接信息
          if (liveRoomDetail.roomLink && store.currentRoomState) {
            store.currentRoomState.roomLink = liveRoomDetail.roomLink;
          }

          // 更新其他直播间信息
          if (liveRoomDetail.platform && store.currentRoomState) {
            store.currentRoomState.livePlatform = liveRoomDetail.platform;
          }
        }
      } catch {
        // 获取详情失败，使用现有配置
      }

      // 3. 初始化音频队列WebSocket（需要sessionId）
      await store.initAudioQueueWebSocket(roomId);

      // 4. 🔧 修复：只有在开播状态下才绑定公屏WebSocket
      if (
        store.currentRoomState.roomLink &&
        store.currentRoomState.productList?.status === "live"
      ) {
        await store.bindCommentWebSocket();
        console.log(
          `🔌 跨浏览器同步：房间${roomId}已开播，自动绑定公屏WebSocket成功`
        );
      } else if (store.currentRoomState.roomLink) {
        console.log(
          `🔌 跨浏览器同步：房间${roomId}有链接但未开播，跳过自动绑定公屏WebSocket`
        );
      }

      // 5. 🔧 已禁用 - 后端不需要请求消息
      // 请求音频开始播放
      // if (store.currentRoomState.queue.length === 0) {
      // }
    } catch {
      ElMessage.error(`直播连接初始化失败`);
    }
  };

  /**
   * 清理直播连接
   */
  const cleanupLiveConnections = () => {
    if (!store.currentRoomState) return;

    // 1. 更新播放状态
    store.currentRoomState.isPlaying = false;

    // 停止音频播放
    if ((store.currentRoomState as any).howlerPlayer) {
      try {
        const howlerPlayer = (store.currentRoomState as any).howlerPlayer;
        howlerPlayer.stop();
        howlerPlayer.destroy();
        (store.currentRoomState as any).howlerPlayer = null;
      } catch (error) {
        (store.currentRoomState as any).howlerPlayer = null;
      }
    }

    // 2. 关闭公屏WebSocket连接
    if (store.currentRoomState.commentWs) {
      try {
        if (store.currentRoomState.commentWs.readyState === WebSocket.OPEN) {
          store.currentRoomState.commentWs.close();
        }
        store.currentRoomState.commentWs = null;
      } catch {
        // 静默处理错误
      }
    }

    // 3. 关闭音频队列WebSocket连接
    if (store.currentRoomState.audioQueueWs) {
      try {
        if (store.currentRoomState.audioQueueWs.readyState === WebSocket.OPEN) {
          store.currentRoomState.audioQueueWs.close();
        }
        store.currentRoomState.audioQueueWs = null;
      } catch {
        // 静默处理错误
      }
    }

    // 4. 清空队列和消息
    if (
      store.currentRoomState.queue &&
      store.currentRoomState.queue.length > 0
    ) {
      store.currentRoomState.queue = [];
      store.currentRoomState.currentIndex = 0;
    }

    // 清理公屏信息
    if (
      store.currentRoomState.publicMessages &&
      store.currentRoomState.publicMessages.length > 0
    ) {
      store.currentRoomState.publicMessages = [];
    }

    // 5. 清除定时器

    // 6. 重置绑定状态和直播信息
    if (store.currentRoomState.isBound) {
      store.currentRoomState.isBound = false;
    }

    // 7. 清理会话信息
    if (store.currentRoomState.sessionId) {
      store.currentRoomState.sessionId = "";
    }
  };

  return {
    setupCrossBrowserSync,
    cleanupCrossBrowserSync,
    handleLiveStatusChange,
    initLiveConnections,
    cleanupLiveConnections
  };
}
